import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { Award, ArrowRight } from "lucide-react";

// Fully-editable Partner / Elite Suppliers block. Data-driven — every field
// (eyebrow, heading, heading link, subtitle, CTA, and the logo list) is
// configured from the CMS, no site_settings dependency. See
// /app/backend/seed_data/batch_e_pages.py for the seed defaults.

function resolveMediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
}

function isExternal(href) {
  return typeof href === "string" && /^https?:\/\//i.test(href);
}

export default function PartnerLogosBlock({ data }) {
  const d = data || {};
  const eyebrow = d.eyebrow || "";
  const heading = d.heading || "";
  const headingHref = d.heading_href || "";
  const subtitle = d.subtitle || "";
  const ctaLabel = d.cta_label || "";
  const ctaHref = d.cta_href || "";
  const speed = Number(d.marquee_speed) > 0 ? Number(d.marquee_speed) : 35;
  const logos = Array.isArray(d.logos)
    ? d.logos.filter((l) => l && l.name && l.logo_url)
    : [];

  const headingContent = heading ? (
    headingHref ? (
      isExternal(headingHref) ? (
        <a
          href={headingHref}
          data-testid="partner-logos-heading-link"
          className="inline-block hover:text-sky-600 transition-colors"
        >
          {heading}
        </a>
      ) : (
        <Link
          to={headingHref}
          data-testid="partner-logos-heading-link"
          className="inline-block hover:text-sky-600 transition-colors"
        >
          {heading}
        </Link>
      )
    ) : (
      heading
    )
  ) : null;

  const ctaContent = ctaLabel && ctaHref ? (
    (() => {
      const cls =
        "btn-premium inline-flex items-center gap-2 mt-6 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold px-6 py-3 shadow-lg shadow-sky-600/25 transition group";
      const inner = (
        <>
          {ctaLabel}
          <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </>
      );
      if (isExternal(ctaHref)) {
        return (
          <a
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="partner-logos-learn-link"
            className={cls}
          >
            {inner}
          </a>
        );
      }
      return (
        <Link to={ctaHref} data-testid="partner-logos-learn-link" className={cls}>
          {inner}
        </Link>
      );
    })()
  ) : null;

  return (
    <section
      data-testid="partner-logos-section"
      className="py-10 lg:py-14 bg-white relative"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-sky-200 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 lg:mb-8">
        <div className="text-center">
          {eyebrow && (
            <span
              data-testid="partner-logos-eyebrow"
              className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.2em] uppercase text-sky-600 bg-sky-50 border border-sky-200 rounded-full px-3 py-1"
            >
              <Award className="h-3 w-3" />
              {eyebrow}
            </span>
          )}
          {heading && (
            <h2
              data-testid="partner-logos-heading"
              className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900"
            >
              {headingContent}
            </h2>
          )}
          {subtitle && (
            <p className="mt-3 text-sm lg:text-base text-slate-500 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
          {ctaContent}
        </div>
      </div>

      {logos.length > 0 && (
        <Marquee speed={speed} gradient gradientColor="#ffffff" gradientWidth={60} pauseOnHover>
          {logos.map((logo, i) => {
            const src = resolveMediaUrl(logo.logo_url);
            const href = logo.brand_url;
            const testId = `logo-${logo.name.toLowerCase().replace(/\s+/g, "-")}`;
            const inner = (
              <div className="w-36 h-36 sm:w-48 sm:h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 flex items-center justify-center p-3 sm:p-4 rounded-lg transition-transform hover:scale-105">
                <img
                  src={src}
                  alt={logo.name}
                  className="marquee-logo max-w-full max-h-full object-contain transition-opacity hover:opacity-80"
                  data-testid={testId}
                  loading="lazy"
                />
              </div>
            );
            if (href) {
              const external = isExternal(href);
              return (
                <a
                  key={`${logo.name}-${i}`}
                  href={href}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noopener noreferrer" : undefined}
                  className="mx-3 sm:mx-4 md:mx-5 flex items-center justify-center cursor-pointer"
                  aria-label={logo.name}
                >
                  {inner}
                </a>
              );
            }
            return (
              <span
                key={`${logo.name}-${i}`}
                className="mx-3 sm:mx-4 md:mx-5 flex items-center justify-center"
                aria-label={logo.name}
              >
                {inner}
              </span>
            );
          })}
        </Marquee>
      )}
    </section>
  );
}
